// ═══════════════════════════════════════════════════════════════════
// ReferralBanner (Apr28 v15)
// ═══════════════════════════════════════════════════════════════════
// Replaces v14's permanent bar with a more polite pattern:
//
//   1. On first page load of a session (with active referral cookie),
//      slide a gold toast bar down from the top → hold 10 seconds →
//      slide up and hide automatically.
//   2. After it hides, a small persistent gold dot stays anchored
//      at the top-right corner. Tapping it re-opens the toast briefly
//      so the customer can verify their click is being tracked.
//   3. Customer can dismiss the toast immediately with ✕ — that just
//      hides the toast for the session, the persistent dot stays so
//      they can re-check anytime.
//   4. The persistent dot never appears on admin pages.
//
// Cookie expiry handling unchanged from v14:
//   - Reads localStorage `gg_referral`
//   - Auto-cleans expired or invalid cookies
//   - Resolves creator name via /api/creators/ref/:code with sessionStorage cache
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
const STORAGE_KEY     = 'gg_referral';
const SHOWN_KEY       = 'gg_referral_banner_shown';      // session flag — banner already auto-shown this session
const NAME_CACHE_KEY  = 'gg_referral_creator_name';
const TOAST_DURATION_MS = 10_000;                         // 10s per Vinay's spec

const ReferralBanner = () => {
  const location = useLocation();
  const [creatorName, setCreatorName] = useState('');
  const [refCode, setRefCode]         = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const hideTimerRef = useRef(null);

  // ─── Load referral cookie + creator info ────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setRefCode('');
        return;
      }
      const data = JSON.parse(raw);
      const code = data?.code || data?.refCode;
      const expiresAt = data?.expiresAt || 0;
      if (!code || !expiresAt || Date.now() > expiresAt) {
        // Expired — clean up
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
        setRefCode('');
        return;
      }
      setRefCode(code);

      // Cached name → no flicker
      const cachedName = sessionStorage.getItem(`${NAME_CACHE_KEY}:${code}`);
      if (cachedName) {
        setCreatorName(cachedName);
        return;
      }

      // Fetch from backend
      fetch(`${API}/api/creators/ref/${encodeURIComponent(code)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.valid && d?.creatorName) {
            setCreatorName(d.creatorName);
            try { sessionStorage.setItem(`${NAME_CACHE_KEY}:${code}`, d.creatorName); } catch (_) {}
          } else if (d && d.valid === false) {
            // Code no longer valid (creator suspended/deleted)
            try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
            setRefCode('');
          }
        })
        .catch(() => {});
    } catch (_) {}
  }, [location.pathname]);

  // ─── Auto-show toast once per session ───────────────────────
  // Triggers on first page load with a valid refCode. After auto-show,
  // toast won't reopen automatically — only via the persistent dot.
  useEffect(() => {
    if (!refCode) return;
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(SHOWN_KEY) === '1'; } catch (_) {}
    if (alreadyShown) return;

    // Mark as shown immediately so route changes within the toast window
    // don't re-trigger it.
    try { sessionStorage.setItem(SHOWN_KEY, '1'); } catch (_) {}

    // Small delay so it slides in AFTER the page settles (not while hero
    // images are still loading and triggering layout shifts).
    const showTimer = setTimeout(() => setToastVisible(true), 600);
    return () => clearTimeout(showTimer);
  }, [refCode]);

  // ─── Auto-hide toast after duration ─────────────────────────
  useEffect(() => {
    if (!toastVisible) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setToastVisible(false), TOAST_DURATION_MS);
    return () => hideTimerRef.current && clearTimeout(hideTimerRef.current);
  }, [toastVisible]);

  const handleDismiss = () => {
    setToastVisible(false);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  // Hide everything on admin routes
  if (location.pathname.startsWith('/admin')) return null;
  if (!refCode) return null;

  const firstName = creatorName ? creatorName.split(' ')[0] : null;

  // Apr28 v17 — persistent gold dot REMOVED. It was visually colliding with
  // the cart icon on mobile (Vinay's screenshot showed the overlap). Decision:
  // toast confirmation on first page load is enough; customers also see
  // attribution again on the checkout page (v14). No need for a persistent
  // re-open mechanism — simpler UI wins.
  return (
    <div
      style={{
        ...S.toast,
        transform: toastVisible ? 'translateY(0)' : 'translateY(-100%)',
        opacity:   toastVisible ? 1 : 0,
        pointerEvents: toastVisible ? 'auto' : 'none',
      }}
      role="status"
      aria-live="polite"
    >
      <span style={S.icon}>💎</span>
      <span style={S.text}>
        {firstName ? (
          <>
            You're shopping with <strong style={S.creator}>{firstName}</strong>'s link
            <span style={S.divider}> · </span>
            <span style={S.muted}>your purchase supports their work ✨</span>
          </>
        ) : (
          <>
            You're shopping with creator code <strong style={S.creator}>{refCode}</strong>
            <span style={S.divider}> · </span>
            <span style={S.muted}>your purchase supports the creator ✨</span>
          </>
        )}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        style={S.dismissBtn}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  );
};

const S = {
  // Toast bar (slide down)
  toast: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(90deg, rgba(201,168,76,0.18), rgba(201,168,76,0.28) 50%, rgba(201,168,76,0.18))',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(201,168,76,0.45)',
    color: '#f5f0e8',
    padding: '10px 44px 10px 18px',
    fontSize: 12.5,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    zIndex: 100,
    flexWrap: 'wrap',
    transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    textAlign: 'center',
  },
  icon: { fontSize: 14 },
  text: { letterSpacing: 0.2 },
  creator: {
    color: '#f0d080',
    fontWeight: 700,
    fontFamily: 'Georgia, "Times New Roman", serif',
    letterSpacing: 0.5,
  },
  divider: { color: 'rgba(201,168,76,0.5)' },
  muted: {
    color: 'rgba(245,240,232,0.7)',
    fontStyle: 'italic',
    fontSize: 11.5,
  },
  dismissBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    color: 'rgba(245,240,232,0.65)',
    fontSize: 14,
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
    fontFamily: 'system-ui',
  },
  // Apr28 v17: dot removed (was visually colliding with cart icon on mobile)
};

export default ReferralBanner;
